import { asLinkAttrs } from '@prismicio/client';
import { PrismicNextLinkProps } from '@prismicio/next';
import clsx from 'clsx';
import Link, { LinkProps } from 'next/link';

import { ButtonHTMLAttributes } from 'react';

import * as styles from './call-to-action.css';

import { IconArrowRight } from './icons/arrow-right';

type BaseCalloutProps = {
  hasArrow?: boolean;
};

type ButtonCallToActionProps = {
  type: 'button' | 'submit';
  label: string;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

type NextCallToActionProps = {
  label: string;
  type: 'next';
  className?: string;
} & Omit<LinkProps, 'children'>;

type PrismicCallToActionProps = {
  type: 'prismic';
} & PrismicNextLinkProps;

export type CallToActionProps = BaseCalloutProps &
  (NextCallToActionProps | PrismicCallToActionProps | ButtonCallToActionProps);

export function CallToAction(props: CallToActionProps) {
  if (props.type === 'next') {
    const { label, className, hasArrow, ...nextProps } = props;
    return (
      <Link {...nextProps} className={clsx(styles.wrapper, className)}>
        <span className={styles.label}>{label}</span>
        {hasArrow !== false && <IconArrowRight className={styles.icon} />}
      </Link>
    );
  } else if (props.type === 'button' || props.type === 'submit') {
    const { className, hasArrow, label, ...buttonProps } = props;
    return (
      <button {...buttonProps} className={clsx(styles.wrapper, className)}>
        <span className={styles.label}>{label}</span>
        {hasArrow !== false && <IconArrowRight className={styles.icon} />}
      </button>
    );
  } else if (props.type === 'prismic') {
    const {
      field,
      document,
      linkResolver,
      children,
      className,
      hasArrow,
      ...restProps
    } = props;

    const {
      href: computedHref,
      rel: computedRel,
      ...attrs
    } = asLinkAttrs(field ?? document, {
      linkResolver,
      rel: typeof restProps.rel === 'function' ? restProps.rel : undefined,
    });

    const href = ('href' in restProps ? restProps.href : computedHref) ?? '';

    let rel = computedRel;
    if ('rel' in restProps && typeof restProps.rel !== 'function') {
      rel = restProps.rel;
    }

    return (
      <Link
        {...attrs}
        {...restProps}
        href={href}
        rel={rel}
        className={clsx(styles.wrapper, className)}
      >
        <span className={styles.label}>
          {'children' in props ? children : field?.text}
        </span>
        {hasArrow !== false && <IconArrowRight className={styles.icon} />}
      </Link>
    );
  }
}
